import React from 'react';
import { ActionButtons, ArgumentsEditor, ResultViewer, SchemaViewer, ToolSelector } from './components/index';
import { Badge } from '../../../../Utility/Badge';
import useMcpTester from './hooks/useMcpTester';
import { RootState, useAppSelector } from '../../../../../store';

interface McpTesterProps {
    preselectedConfigIdentifier?: string;
    resetSignal?: any;
}

const McpTester: React.FC<McpTesterProps> = ({ preselectedConfigIdentifier, resetSignal }) => {
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const {
        selectedConfig,
        selectedTool,
        mcpTools,
        setSelectedTool,
        toolArguments,
        setToolArguments,
        result,
        isLoading,
        selectedToolData,
        executeTool,
        clearResults,
        canExecute,
        setCanExecute,
    } = useMcpTester({ preselectedConfigIdentifier, resetSignal });

    return (
        <div className="flex flex-col gap-6">
            <div>
                <h4 className="mb-2">{i18n.translate({ id: 'tester.title', sourceName: 'MCP' })}</h4>
                {!selectedConfig && (
                    <Badge className="mt-4" variant="info">
                        <div>
                            <p>
                                Bitte füllen Sie mindestens die folgenden Felder aus und speichern Sie die
                                Konfiguration, um sie anschließend hier zu testen.
                            </p>
                            <ul className="p mb-0 py-0">
                                <li>Titel</li>
                                <li>Server-URL</li>
                            </ul>
                        </div>
                    </Badge>
                )}
            </div>

            {selectedConfig && (
                <div className="">
                    <div className="bg-layout-backgroundColor">
                        <h5 className="text-layout-textcolor mb-4">
                            {i18n.translate({ id: 'tester.section.execute', sourceName: 'MCP' })}
                        </h5>

                        <div className="space-y-4">
                            <ToolSelector
                                label={i18n.translate({ id: 'tester.tool.label', sourceName: 'MCP' })}
                                value={selectedTool}
                                onChange={setSelectedTool}
                                disabled={!selectedConfig}
                                placeholder={i18n.translate({ id: 'tester.tool.placeholder', sourceName: 'MCP' })}
                                items={mcpTools.map((tool) => ({
                                    value: tool.identifier,
                                    label: tool.name,
                                    subLabel: tool.description,
                                }))}
                            />

                            {selectedToolData && (
                                <SchemaViewer
                                    title={i18n.translate({ id: 'tester.schema', sourceName: 'MCP' })}
                                    schema={selectedToolData.inputSchema}
                                />
                            )}

                            {selectedToolData && (
                                <ArgumentsEditor
                                    title={i18n.translate({ id: 'tester.arguments', sourceName: 'MCP' })}
                                    value={toolArguments}
                                    onChange={setToolArguments}
                                    placeholder={i18n.translate({
                                        id: 'tester.arguments.placeholder',
                                        sourceName: 'MCP',
                                    })}
                                    setCanExecute={setCanExecute}
                                    schema={selectedToolData?.inputSchema}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}

            <ResultViewer title={i18n.translate({ id: 'tester.result', sourceName: 'MCP' })} result={result} />

            <ActionButtons
                executeLabel={i18n.translate({ id: 'tester.execute', sourceName: 'MCP' })}
                executingLabel={i18n.translate({ id: 'tester.executing', sourceName: 'MCP' })}
                resetLabel={i18n.translate({ id: 'tester.reset', sourceName: 'MCP' })}
                canExecute={Boolean(selectedTool) && !isLoading && canExecute}
                isLoading={isLoading}
                hasResult={Boolean(result)}
                onExecute={executeTool}
                onReset={clearResults}
            />
        </div>
    );
};

export default McpTester;
